import clsx from 'clsx';
import selectStyles from './Select.module.css';
import styles from './MultiSelect.module.css';
import pillStyles from '../pill/Pill.module.css';
import { OverflowPills } from '../overflow-pills';
import { ChevronDownIcon } from '../icons';
import { MultiSelectMenu } from './components/MultiSelectMenu';
import {
  useMultiSelectController,
  getSelectChevronClassName,
  getSelectTriggerClassName,
  getSelectValueClassName,
  getValuePillsRowMaxStyle,
} from './lib';

export function MultiSelect({
  values,
  options,
  onChange,
  inputId,
  ariaLabel = 'Select',
  placeholder = 'Select',
  disabled = false,
  className = '',
  variant = 'default',
  menuPlacement = 'bottom',
  menuWidth = 'content',
  filledBg = 'tinted',
  invalid = false,
  maxMenuRows,
  showOptionCheckboxes = false,
  menuWidthPx,
  valueMaxRows = 1,
  selectAllValue = null,
}) {
  const {
    listboxId,
    rootRef,
    buttonRef,
    open,
    normalizedOptions,
    normalizedValues,
    labelStrings,
    selectionIdentityKey,
    highlightedIndex,
    setHighlightedIndex,
    isEmpty,
    forceSurfaceBg,
    toggleValue,
    onTriggerKeyDown,
    onTriggerClick,
    allSelected,
    someSelected,
  } = useMultiSelectController({
    values,
    options,
    onChange,
    variant,
    filledBg,
    valueMaxRows,
    selectAllValue,
  });

  const pillsRowMaxStyle = getValuePillsRowMaxStyle(valueMaxRows);

  return (
    <div ref={rootRef} className={clsx(selectStyles.root, className)}>
      <button
        id={inputId}
        ref={buttonRef}
        type="button"
        className={getSelectTriggerClassName({ variant, isEmpty, forceSurfaceBg, open, invalid })}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-invalid={invalid ? 'true' : undefined}
        disabled={disabled}
        onClick={disabled ? undefined : onTriggerClick}
        onKeyDown={disabled ? undefined : onTriggerKeyDown}
      >
        <span className={getSelectValueClassName({ variant, isEmpty: false })}>
          {isEmpty ? (
            <span className={clsx(selectStyles.valuePlaceholder)}>{placeholder}</span>
          ) : (
            <OverflowPills
              items={labelStrings}
              wrapClassName={styles.pillsWrap}
              className={styles.pillsRow}
              pillClassName={clsx(pillStyles.pill, pillStyles.nowrap, pillStyles.flexNone)}
              maxRows={valueMaxRows}
              identityKey={selectionIdentityKey}
              rowStyle={pillsRowMaxStyle}
              showOverflowTooltip={false}
            />
          )}
        </span>
        <ChevronDownIcon className={getSelectChevronClassName({ variant, open })} aria-hidden />
      </button>

      {open && !disabled ? (
        <MultiSelectMenu
          listboxId={listboxId}
          ariaLabel={ariaLabel}
          menuPlacement={menuPlacement}
          menuWidth={menuWidth}
          menuWidthPx={menuWidthPx}
          maxMenuRows={maxMenuRows}
          options={normalizedOptions}
          normalizedValues={normalizedValues}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          toggleValue={toggleValue}
          showOptionCheckboxes={showOptionCheckboxes}
          selectAllValue={selectAllValue}
          allSelected={allSelected}
          someSelected={someSelected}
        />
      ) : null}
    </div>
  );
}
